import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import DataMatrixInput from '../../components/form/DataMatrixInput.tsx';
import queryHooks from '../../common/queryHooks/index.ts';
import { MdDeleteOutline } from 'react-icons/md';
import { MedAPI } from '../../common/api/med.ts';
import { sendPedigreeFinalReviewData } from './SendPedigree.tsx';
import MedSearchModal from './Modals/MedSearchModal.tsx';

interface IProps {
  customerLocationId: number | string;
  setOutboundMeds: Dispatch<
    SetStateAction<MedAPI.Response.ReadOneBySerialNumberPedigree[]>
  >;
  setFinalReviewData: Dispatch<SetStateAction<sendPedigreeFinalReviewData>>;
}
interface ScanData {
  serialNumber: string;
  expiration: string;
  lotNumber: string;
  gtin: string;
}

const MedList = ({
  setOutboundMeds: setParentMeds,
  setFinalReviewData,
  customerLocationId,
}: IProps) => {
  const [isOpenSearchModal, setIsOpenSearchModal] = useState<boolean>(false);
  const [scanData, setScanData] = useState<ScanData>();
  const [outboundMeds, setOutboundMeds] = useState<
    MedAPI.Response.ReadOneBySerialNumberPedigree[]
  >([]);

  const onScan = (data: { [key: string]: string }) => {
    const serialNumber = data[`SERIAL NUMBER`];
    const expiration = data[`EXPIRATION DATE (YYMMDD)`];
    const lotNumber = data[`BATCH/LOT NUMBER`];
    const gtin = data[`GLOBAL TRADE ITEM NUMBER (GTIN)`];
    setScanData({ serialNumber, expiration, lotNumber, gtin });
  };

  const { data: med, isLoading: isLoadingMed } =
    queryHooks.med.useReadOneBySerialNumberPedigree({
      locationId: customerLocationId,
      serialNumber: scanData?.serialNumber || '',
      lotNumber: scanData?.lotNumber,
    });

  useEffect(() => {
    if (
      med &&
      !outboundMeds.find(outboundMed => outboundMed.med.id === med.med.id)
    )
      setOutboundMeds([...outboundMeds, med]);

    setScanData(undefined);
  }, [med]);

  const removeOutboundMed = (id: number) => {
    const index = outboundMeds.findIndex(({ med }) => med.id === id);
    outboundMeds.splice(index, 1);
    setOutboundMeds([...outboundMeds]);
  };

  useEffect(() => {
    const deDuplicated = outboundMeds.filter(
      (item, index, self) =>
        self.findIndex(val => val.med.id === item.med.id) === index
    );

    if (deDuplicated.length !== outboundMeds.length)
      setOutboundMeds([...deDuplicated]);

    setParentMeds([...outboundMeds]);
    setFinalReviewData(prev => ({ ...prev, meds: outboundMeds }));
  }, [outboundMeds]);

  return (
    <div className="card">
      <div className="card-content">
        <div className="row">
          <h6>Med List</h6>
          <div className="divider" />
        </div>
        <div className="row">
          <div className="input-field col s12 m8 l5">
            <DataMatrixInput
              id="sgtin"
              onScan={onScan}
              value={scanData?.serialNumber || ''}
              isLoading={isLoadingMed}
            />
            <label htmlFor="sgtin">Scan Med</label>
          </div>
          <a
            className="right"
            href="/"
            onClick={ev => {
              ev.preventDefault();
              setIsOpenSearchModal(true);
            }}
          >
            Advanced Search
          </a>
        </div>
        <div className="row">
          <table className="highlight">
            <thead>
              <tr>
                <th>Med Name</th>
                <th>Manufacturer</th>
                <th>NDC</th>
                <th>Serial Number</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {outboundMeds.map(({ med, redbookDetail, redbook }) => (
                <tr key={med.id}>
                  <td style={{ padding: '4px' }}>{redbookDetail.medName}</td>
                  <td style={{ padding: '4px' }}>
                    {redbookDetail.manufacturerName}
                  </td>
                  <td style={{ padding: '4px' }}>{redbook.ndc}</td>
                  <td style={{ padding: '4px' }}>
                    {med.serialNumber.split('.').pop()}
                  </td>
                  <td style={{ padding: '4px' }}>
                    <MdDeleteOutline
                      onClick={() => removeOutboundMed(med.id)}
                      className="red-text"
                      style={{ fontSize: '1.2em', cursor: 'pointer' }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <MedSearchModal
        isOpen={isOpenSearchModal}
        setIsOpen={setIsOpenSearchModal}
        customerLocationId={customerLocationId as number}
        setOutboundMeds={setOutboundMeds}
      />
    </div>
  );
};

export default MedList;
